/*
 *
 * LanguageProvider
 *
 * this component connects the redux state language locale to the
 * IntlProvider component and i18n messages (loaded from `app/translations`)
 */

import { Children, memo, useEffect } from "react";

import { DateTime, Settings } from "luxon";
import PropTypes from "prop-types";
import { IntlProvider } from "react-intl";
import { createStructuredSelector } from "reselect";

import { connect } from "react-redux";

import { selectCustomText } from "containers/Shared/App/selectors";
import { changeLocale } from "containers/Shared/LanguageProvider/actions";
import GlobalLanguageProvider from "containers/Shared/LanguageProvider/GlobalLanguageProvider";
import { useInjectSaga } from "utils/injectSaga";
import {
  getLocaleObjectFromLocaleString,
  getLocaleStringFromLocaleObject,
} from "utils/localeHelpers";
import LocaleService from "utils/localeService";
import { switchWeglotLang } from "utils/weglot";
import { selectLocale } from "./selectors";
import saga from "./saga";

export function LanguageProvider(props) {
  useInjectSaga({ key: "language", saga });

  // Gets the browser locale
  const defaultBrowserLocale =
    DateTime.local() &&
    DateTime.local().resolvedLocaleOptions() &&
    DateTime.local().resolvedLocaleOptions().locale;
  // Get the Intl.Locale object using the current set locale, the browser locale if the locale
  // isn't set, or falls back to en-US
  const userLocaleObject = getLocaleObjectFromLocaleString(
    LocaleService.getLocale() || defaultBrowserLocale || "en-US",
  );
  // Get the locale string (Ex. 'en-US') from the locale object
  const userLocale = getLocaleStringFromLocaleObject(userLocaleObject);
  // Set default language to English.
  const enLocale = "en";

  const messages = { ...props.messages[enLocale] };

  useEffect(() => {
    // Set user locale
    if (userLocale && props.changeLocale) {
      props.changeLocale(userLocale);
      Settings.defaultLocale = userLocale;
      switchWeglotLang(userLocale);
    }
  }, [userLocale]);

  useEffect(() => {
    if (props.customTexts) {
      Object.keys(props.customTexts).forEach((key) => {
        messages[`diverst.texts.${key}`] = props.customTexts[key];
      });
    }
  }, [props.customTexts]);

  return (
    <IntlProvider locale={enLocale} messages={messages}>
      <GlobalLanguageProvider>
        {Children.only(props.children)}
      </GlobalLanguageProvider>
    </IntlProvider>
  );
}

LanguageProvider.propTypes = {
  locale: PropTypes.string,
  messages: PropTypes.object,
  children: PropTypes.element.isRequired,
  customTexts: PropTypes.object,
  changeLocale: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  locale: selectLocale(),
  customTexts: selectCustomText(),
});

const mapDispatchToProps = {
  changeLocale,
};

export default memo(
  connect(mapStateToProps, mapDispatchToProps)(LanguageProvider),
);
